import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/common/components/Label.tsx'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import { PlusIcon } from '@/common/icons/arrows.tsx'
import { parseRequestError } from '@/common/utilities.ts'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog.tsx'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { ProviderCategory } from '@/queries/providers/categories/schema.ts'
import { usePostProviderCategory } from '@/queries/providers/categories/hooks.ts'

const createCategorySchema = (categories: ProviderCategory[]) =>
  z.object({
    categoryName: z
      .string()
      .min(2, { message: 'Category name must be at least 2 characters long' })
      .refine(
        (name) =>
          !categories.some(
            (category) => category.name.toLowerCase() === name.toLowerCase()
          ),
        { message: 'Category name already exists' }
      ),
  })

type CategoryFormValues = {
  categoryName: string
  productType: 'service' | 'good'
}

interface Props {
  onAddCategorySuccess: () => void
  categories: ProviderCategory[]
}

function AddCategoryPopup(props: Props) {
  const { onAddCategorySuccess, categories } = props
  const { toast } = useToast()
  const [open, setOpen] = useState(false)

  const categorySchema = createCategorySchema(categories)

  const createCategory = usePostProviderCategory({
    onSuccess: () => {
      toast({
        title: 'Category Created',
        description: 'The category has been created successfully.',
        variant: 'default',
      })
      onAddCategorySuccess()
      setOpen(false)
      reset()
    },
    onError: (error) => {
      const errorMessage = parseRequestError(error).message
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      categoryName: '',
      productType: 'service',
    },
    context: { categories },
  })

  const onSubmit = (data: CategoryFormValues) => {
    createCategory.mutate({
      name: data.categoryName,
    })
  }

  const handleClose = () => {
    setOpen(false)
    reset()
  }

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger>
          <Button className="h-10 w-10 rounded-full">
            <PlusIcon />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <h2 className="font-medium">Add New Category</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label>Category Name</Label>
              <Input
                {...register('categoryName')}
                aria-invalid={errors.categoryName ? 'true' : 'false'}
              />
              {errors.categoryName && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.categoryName.message}
                </p>
              )}
            </div>
            <div className="flex space-x-2 pt-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <div>
                <SubmitButton
                  buttonTxt="Add Category"
                  status={createCategory.status}
                />
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AddCategoryPopup
